export type ToolStep = {
  id: string
  kind: 'tool'
  tool: string
  args: Record<string, unknown>
}

export type AiStep = {
  id: string
  kind: 'ai_step'
  prompt: string
  model?: string
}

export type ConditionStep = {
  id: string
  kind: 'condition'
  expr: string
  then: string
  else: string
}

export type TransformStep = {
  id: string
  kind: 'transform'
  assign: Record<string, string>
}

export type EndStep = {
  id: string
  kind: 'end'
}

export type Step = ToolStep | AiStep | ConditionStep | TransformStep | EndStep

export type SerializedGraphNode =
  | { id: string; kind: 'tool'; tool: string; args?: Record<string, unknown>; next: string }
  | { id: string; kind: 'ai_step'; prompt: string; model?: string; next: string }
  | { id: string; kind: 'condition'; expr: string; then: string; else: string }
  | { id: string; kind: 'transform'; assign: Record<string, string>; next: string }
  | { id: string; kind: 'end' }

export type SerializedGraph = {
  entry: string
  nodes: SerializedGraphNode[]
}

export type StepValidationIssue = {
  stepId?: string
  message: string
}

const MAX_STEPS = 50

export function stepsToGraph(inputSteps: Step[]): SerializedGraph {
  const steps = withEndStep(inputSteps)
  const nodes = steps.map((step, index): SerializedGraphNode => {
    if (step.kind === 'end') return { id: step.id, kind: 'end' }
    if (step.kind === 'condition') {
      return { id: step.id, kind: 'condition', expr: step.expr, then: step.then, else: step.else }
    }

    const next = steps[index + 1]?.id ?? ensureUniqueEndId(steps)
    if (step.kind === 'tool') {
      return {
        id: step.id,
        kind: 'tool',
        tool: step.tool,
        args: step.args,
        next,
      }
    }
    if (step.kind === 'ai_step') {
      return {
        id: step.id,
        kind: 'ai_step',
        prompt: step.prompt,
        ...(step.model ? { model: step.model } : {}),
        next,
      }
    }
    return {
      id: step.id,
      kind: 'transform',
      assign: step.assign,
      next,
    }
  })

  return {
    entry: steps[0]?.id ?? 'end',
    nodes,
  }
}

export function graphToSteps(graph: unknown): Step[] | { error: string } {
  const parsed = parseGraph(graph)
  if ('error' in parsed) return parsed

  const { nodes, entry } = parsed
  const nodesById = new Map(nodes.map((node) => [node.id, node]))
  const entryIndex = nodes.findIndex((node) => node.id === entry)
  if (entryIndex === -1) return { error: `Unknown entry node: ${entry}` }
  if (entryIndex !== 0) return { error: 'Entry must be the first step for the builder.' }

  const reachable = new Set<string>()
  const visiting = new Set<string>()
  const cycle = findCycle(entry, nodesById, reachable, visiting)
  if (cycle) return { error: "Steps can't loop back to earlier steps." }

  const unreachable = nodes.find((node) => !reachable.has(node.id))
  if (unreachable) return { error: `Unreachable step: ${unreachable.id}` }

  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index]
    if (!hasLinearNext(node)) continue
    const expectedNext = nodes[index + 1]?.id
    if (node.next !== expectedNext) {
      return { error: `Step ${node.id} does not flow to the next visible step.` }
    }
  }

  return nodes.map(nodeToStep)
}

export function validateSteps(steps: Step[]): string[] {
  return validateStepIssues(steps).map((issue) => issue.message)
}

export function validateStepIssues(inputSteps: Step[]): StepValidationIssue[] {
  const steps = withEndStep(inputSteps)
  const issues: StepValidationIssue[] = []
  const ids = new Set<string>()
  const duplicateIds = new Set<string>()

  steps.forEach((step) => {
    if (!step.id.trim()) {
      issues.push({ stepId: step.id, message: 'Step id is required.' })
      return
    }
    if (ids.has(step.id)) {
      duplicateIds.add(step.id)
      issues.push({ stepId: step.id, message: 'Step id already used.' })
    }
    ids.add(step.id)
  })

  if (steps.length > MAX_STEPS) {
    issues.push({ message: 'Commands are capped at 50 steps.' })
  }

  steps.forEach((step) => {
    if (step.kind === 'tool' && !step.tool.trim()) {
      issues.push({ stepId: step.id, message: 'Pick a tool.' })
    }
    if (step.kind === 'ai_step' && !step.prompt.trim()) {
      issues.push({ stepId: step.id, message: 'Prompt is required.' })
    }
    if (step.kind === 'transform' && Object.keys(step.assign).length === 0) {
      issues.push({ stepId: step.id, message: 'Add at least one variable.' })
    }
    if (step.kind === 'condition') {
      if (!step.expr.trim()) {
        issues.push({ stepId: step.id, message: 'Condition is required.' })
      }
      if (!ids.has(step.then) || duplicateIds.has(step.then)) {
        issues.push({ stepId: step.id, message: "Then path points to a step that doesn't exist." })
      }
      if (!ids.has(step.else) || duplicateIds.has(step.else)) {
        issues.push({ stepId: step.id, message: "Else path points to a step that doesn't exist." })
      }
    }
  })

  const graph = stepsToGraph(steps)
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]))
  const reachable = new Set<string>()
  if (findCycle(graph.entry, nodesById, reachable, new Set())) {
    issues.push({ message: "Steps can't loop back to earlier steps." })
  }

  return issues
}

export function defaultSteps(): Step[] {
  return [{ id: 'step1', kind: 'end' }]
}

export function createStep(kind: Step['kind'], existingSteps: Step[]): Step {
  const id = nextStepId(existingSteps)
  if (kind === 'tool') return { id, kind, tool: 'get_budget_status', args: {} }
  if (kind === 'ai_step') return { id, kind, prompt: '' }
  if (kind === 'condition') {
    const fallbackTarget = existingSteps.find((step) => step.id !== id)?.id ?? ''
    return { id, kind, expr: '', then: fallbackTarget, else: fallbackTarget }
  }
  if (kind === 'transform') return { id, kind, assign: {} }
  return { id, kind: 'end' }
}

export function nextStepId(steps: Step[]): string {
  const ids = new Set(steps.map((step) => step.id))
  for (let index = 1; index <= MAX_STEPS + 1; index += 1) {
    const id = `step${index}`
    if (!ids.has(id)) return id
  }
  return `step${Date.now()}`
}

function withEndStep(steps: Step[]): Step[] {
  if (steps.length === 0) return defaultSteps()
  if (steps.some((step) => step.kind === 'end')) return steps
  return [...steps, { id: ensureUniqueEndId(steps), kind: 'end' }]
}

function ensureUniqueEndId(steps: Step[]): string {
  const ids = new Set(steps.map((step) => step.id))
  if (!ids.has('end')) return 'end'
  if (!ids.has('done')) return 'done'
  return nextStepId(steps)
}

function parseGraph(graph: unknown): SerializedGraph | { error: string } {
  if (!isRecord(graph)) return { error: 'Deterministic graph must be an object' }
  if (typeof graph.entry !== 'string' || !graph.entry) return { error: 'Deterministic graph entry is required' }
  if (!Array.isArray(graph.nodes)) return { error: 'Deterministic graph nodes must be an array' }

  const ids = new Set<string>()
  const nodes: SerializedGraphNode[] = []
  for (const rawNode of graph.nodes) {
    if (!isRecord(rawNode)) return { error: 'Graph node must be an object' }
    if (typeof rawNode.id !== 'string' || !rawNode.id) return { error: 'Graph node id is required' }
    if (ids.has(rawNode.id)) return { error: `Duplicate graph node id: ${rawNode.id}` }
    ids.add(rawNode.id)

    if (rawNode.kind === 'tool') {
      if (typeof rawNode.tool !== 'string') return { error: `Tool node ${rawNode.id} requires tool` }
      if (typeof rawNode.next !== 'string' || !rawNode.next) return { error: `Tool node ${rawNode.id} requires next` }
      if (rawNode.args !== undefined && !isRecord(rawNode.args)) return { error: `Tool node ${rawNode.id} args must be an object` }
      nodes.push({ id: rawNode.id, kind: 'tool', tool: rawNode.tool, args: rawNode.args, next: rawNode.next })
      continue
    }
    if (rawNode.kind === 'ai_step') {
      if (typeof rawNode.prompt !== 'string') return { error: `AI step node ${rawNode.id} requires prompt` }
      if (rawNode.model !== undefined && typeof rawNode.model !== 'string') return { error: `AI step node ${rawNode.id} model must be a string` }
      if (typeof rawNode.next !== 'string' || !rawNode.next) return { error: `AI step node ${rawNode.id} requires next` }
      nodes.push({ id: rawNode.id, kind: 'ai_step', prompt: rawNode.prompt, ...(rawNode.model ? { model: rawNode.model } : {}), next: rawNode.next })
      continue
    }
    if (rawNode.kind === 'condition') {
      if (typeof rawNode.expr !== 'string') return { error: `Condition node ${rawNode.id} requires expr` }
      if (typeof rawNode.then !== 'string' || !rawNode.then) return { error: `Condition node ${rawNode.id} requires then` }
      if (typeof rawNode.else !== 'string' || !rawNode.else) return { error: `Condition node ${rawNode.id} requires else` }
      nodes.push({ id: rawNode.id, kind: 'condition', expr: rawNode.expr, then: rawNode.then, else: rawNode.else })
      continue
    }
    if (rawNode.kind === 'transform') {
      if (!isStringRecord(rawNode.assign)) return { error: `Transform node ${rawNode.id} requires string assignments` }
      if (typeof rawNode.next !== 'string' || !rawNode.next) return { error: `Transform node ${rawNode.id} requires next` }
      nodes.push({ id: rawNode.id, kind: 'transform', assign: rawNode.assign, next: rawNode.next })
      continue
    }
    if (rawNode.kind === 'end') {
      nodes.push({ id: rawNode.id, kind: 'end' })
      continue
    }
    return { error: `Unknown graph node kind for ${rawNode.id}` }
  }

  if (!ids.has(graph.entry)) return { error: `Unknown entry node: ${graph.entry}` }
  for (const node of nodes) {
    for (const target of targetsFor(node)) {
      if (!ids.has(target)) return { error: `Unknown node target from ${node.id}: ${target}` }
    }
  }

  return { entry: graph.entry, nodes }
}

function findCycle(
  id: string,
  nodesById: Map<string, SerializedGraphNode>,
  visited: Set<string>,
  visiting: Set<string>,
): boolean {
  if (visiting.has(id)) return true
  if (visited.has(id)) return false
  const node = nodesById.get(id)
  if (!node) return false

  visiting.add(id)
  for (const target of targetsFor(node)) {
    if (findCycle(target, nodesById, visited, visiting)) return true
  }
  visiting.delete(id)
  visited.add(id)
  return false
}

function nodeToStep(node: SerializedGraphNode): Step {
  if (node.kind === 'tool') return { id: node.id, kind: node.kind, tool: node.tool, args: node.args ?? {} }
  if (node.kind === 'ai_step') return { id: node.id, kind: node.kind, prompt: node.prompt, ...(node.model ? { model: node.model } : {}) }
  if (node.kind === 'condition') return { id: node.id, kind: node.kind, expr: node.expr, then: node.then, else: node.else }
  if (node.kind === 'transform') return { id: node.id, kind: node.kind, assign: node.assign }
  return { id: node.id, kind: 'end' }
}

function hasLinearNext(node: SerializedGraphNode): node is Extract<SerializedGraphNode, { next: string }> {
  return node.kind === 'tool' || node.kind === 'ai_step' || node.kind === 'transform'
}

function targetsFor(node: SerializedGraphNode): string[] {
  if (hasLinearNext(node)) return [node.next]
  if (node.kind === 'condition') return [node.then, node.else]
  return []
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return isRecord(value) && Object.values(value).every((entry) => typeof entry === 'string')
}
